import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  readLinkToken,
  submitTransmissionDocuments,
  verifyTransmissionLink,
} from '../services/transmissionLinkService';

// Page publique du lien de transmission (PF-03 CP4b-1 et CP4b-2, DEC-039). Aucun
// compte : le jeton est lu une fois dans le fragment de l'URL (K128), retiré de la
// barre d'adresse, gardé en mémoire (RULES §1.6), vérifié par
// verify-transmission-link puis envoyé avec les pièces à
// submit-transmission-documents. Seuls contrôles : les deux champs fichier et le
// bouton d'envoi (Réessayer sur l'écran d'erreur). Aucun champ texte : le code
// 24 mots n'est saisi qu'à la libération (CP5), dans le navigateur, et n'est
// jamais envoyé (Q2, K124).

const BG   = '#0A1628';
const S    = '#00F5A0';
const G    = '#8892A4';
const CARD = 'rgba(255,255,255,0.04)';
const BORD = 'rgba(255,255,255,0.1)';
const WARN = '#F59E0B';
const ERR  = '#EF4444';

const formatLongDate = (iso) =>
  new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });

// Textes CTO, provisoires jusqu'à T-S5-00 (écran valide, dépôt, accusé de
// réception, examen, erreurs de dépôt, écrans d'avis). Sans prénom du titulaire,
// « de [prénom] » devient « SafeHeir » et « [prénom] » devient « la personne qui
// vous a désigné(e) ».
const titleFor = (owner) =>
  (owner ? `Procédure de transmission de ${owner}` : 'Procédure de transmission SafeHeir');

const introFor = (owner) => `${owner
  ? `${owner} vous a désigné(e) comme bénéficiaire d'une partie de son coffre SafeHeir.`
  : "Vous avez été désigné(e) comme bénéficiaire d'une partie d'un coffre SafeHeir."
} Cette page vous accompagne dans la procédure de transmission.`;

const validityFor = (expiresAt) =>
  `Ce lien reste valable 60 jours après chaque visite, actuellement jusqu'au ${formatLongDate(expiresAt)}.`;

const reportFor = (owner) =>
  `Si ${owner || 'la personne qui vous a désigné(e)'} est en vie, ne poursuivez pas cette procédure et écrivez à contact@safeheir.fr.`;

const DOCUMENTS = [
  "une copie intégrale de l'acte de décès, délivrée par la mairie ;",
  "une pièce d'identité en cours de validité à votre nom.",
];

const DEPOSIT = {
  title: 'Déposer vos documents',
  intro: 'Déposez les deux documents en une seule fois. Une fois envoyés, ils ne peuvent plus être modifiés.',
  certificateLabel: "Copie intégrale de l'acte de décès",
  identityLabel: "Votre pièce d'identité",
  formats: 'Formats acceptés : PDF, JPEG ou PNG, 10 Mo maximum par fichier.',
  // Information art. 13 (K143), provisoire jusqu'à l'avis du cabinet (T-S5-00) ; base légale 6.1.f non validée.
  privacyLead: {
    before: 'En envoyant vos documents, vous les transmettez à SafeHeir, responsable de traitement '
      + '(identité et coordonnées complètes dans nos ',
    link: 'Mentions légales',
    after: '), dans le seul but de vérifier le décès et votre identité avant toute transmission.',
  },
  privacy: [
    "Ce traitement repose sur l'intérêt légitime de SafeHeir à exécuter les volontés de la personne qui "
      + 'vous a désigné(e) et à prévenir la fraude (article 6.1.f du RGPD).',
    'Le dépôt de ces deux documents est indispensable : sans eux, la transmission ne peut pas avoir lieu. '
      + "La décision est prise par une personne de l'équipe SafeHeir ; aucune décision n'est entièrement "
      + 'automatisée.',
    "SafeHeir enregistre aussi la date d'envoi et une empreinte non réversible de votre adresse IP et de "
      + 'votre navigateur, pour la sécurité du service.',
    "Vos documents sont hébergés sur des serveurs situés dans l'Union européenne par un prestataire "
      + 'sous-traitant, et ne sont communiqués à aucun autre tiers, sauf obligation légale ou signalement '
      + "d'une fraude aux autorités.",
    "Votre pièce d'identité est effacée automatiquement 30 jours après le dépôt. L'acte de décès est "
      + 'conservé le temps de la procédure, puis à titre de preuve de la transmission.',
    "Vous pouvez exercer vos droits d'accès, de rectification, d'opposition et d'effacement à "
      + 'contact@safeheir.fr, et adresser une réclamation à la CNIL (cnil.fr).',
  ],
  submit: 'Envoyer les documents',
  sending: 'Envoi en cours, ne fermez pas cette page...',
};

const NO_ACTION = "Aucune action n'est attendue de votre part pour l'instant.";
const REPORT_MISTAKE = 'Si vous constatez une erreur dans les documents envoyés, écrivez à contact@safeheir.fr.';

const ACK = {
  title: 'Documents reçus',
  received: (date) => `Vos documents ont bien été reçus le ${date}. Ils vont être examinés par SafeHeir.`,
  next: `${NO_ACTION} Vous pouvez rouvrir ce lien depuis votre email pour suivre votre dossier.`,
};

const REVIEW = {
  title: "Dossier en cours d'examen",
  received: (date) => `Vos documents ont été reçus le ${date} et sont en cours d'examen.`,
};

const retryIn = (minutes) => `Réessayez dans ${minutes} minute(s).`;

const TOO_LARGE = "L'un des fichiers dépasse 10 Mo. Réduisez sa taille ou choisissez un autre fichier.";
const UNCONFIRMED = "L'envoi n'a pas pu être confirmé. Réessayez dans quelques instants ; "
  + 'si le problème persiste, écrivez à contact@safeheir.fr.';

const DEPOSIT_ERRORS = {
  missing_file: "Ajoutez les deux documents avant d'envoyer.",
  empty_file: "L'un des fichiers est vide. Choisissez un autre fichier.",
  file_too_large: TOO_LARGE,
  payload_too_large: TOO_LARGE,
  unsupported_file_type: 'Format non accepté. Envoyez un fichier PDF, JPEG ou PNG.',
  already_submitted: 'Des documents ont déjà été déposés avec ce lien. '
    + "Rouvrez le lien depuis votre email pour voir l'état de votre dossier.",
  rate_limited: (minutes) => `Trop de tentatives. ${retryIn(minutes)}`,
  unavailable: UNCONFIRMED,
  error: UNCONFIRMED,
};

// Refus du lien pendant un dépôt : écran d'avis, mêmes textes qu'à la vérification.
const LINK_REFUSALS = ['invalid', 'gone', 'not_eligible'];

const CODE_NOTICE = {
  title: 'Votre code de transmission reste chez vous',
  text: 'SafeHeir ne vous demandera jamais votre code de transmission par email, par téléphone ni par message. '
    + "Il vous sera demandé uniquement sur cette page sécurisée, à l'étape finale, et ne quittera jamais "
    + "votre appareil. Aucun code n'est nécessaire pour déposer vos documents : d'ici là, conservez-le hors ligne.",
};

// Mention art. 13 : pied de page du mail de transmission, mot pour mot, plus la
// date de passage enregistrée à chaque visite.
const FOOTER = [
  'SafeHeir — Responsable de traitement des données à caractère personnel.',
  'Vos coordonnées ont été transmises par la personne vous ayant désigné(e), dans le seul cadre '
    + 'du service de transmission du patrimoine numérique SafeHeir.',
  "Conformément au RGPD, vous disposez d'un droit d'accès, de rectification, d'opposition et "
    + "d'effacement sur vos données. Pour exercer ces droits : contact@safeheir.fr",
  'À chaque visite, SafeHeir enregistre la date de votre passage pour prolonger la validité du lien.',
];

const NOTICES = {
  invalid: {
    tone: ERR,
    title: "Ce lien n'est pas reconnu",
    text: "Rouvrez le lien depuis l'email « Procédure de transmission » que vous avez reçu. "
      + "Si vous avez rechargé cette page, c'est normal : le lien doit être ouvert directement depuis l'email.",
  },
  gone: {
    tone: WARN,
    title: "Ce lien n'est plus valable",
    text: 'Il a expiré ou a été remplacé par un lien plus récent. Vérifiez votre boîte de réception, '
      + "puis écrivez à contact@safeheir.fr si vous n'avez pas reçu de nouveau lien.",
  },
  not_eligible: {
    tone: WARN,
    title: 'Procédure indisponible',
    text: 'Cette procédure ne peut pas être poursuivie pour le moment. Écrivez à contact@safeheir.fr '
      + 'en indiquant le nom de la personne qui vous a désigné(e).',
  },
  error: {
    tone: ERR,
    title: 'Une erreur est survenue',
    text: "Nos serveurs n'ont pas pu répondre. Réessayez dans quelques instants.",
  },
  rate_limited: {
    tone: WARN,
    title: 'Trop de tentatives',
    text: (minutes) => `Pour protéger votre dossier, l'accès est temporairement limité. ${retryIn(minutes)}`,
  },
};

// unavailable : même écran que error, avec Réessayer.
const noticeFor = (result) => {
  if (result.outcome === 'rate_limited') {
    return { ...NOTICES.rate_limited, text: NOTICES.rate_limited.text(result.retryAfterMinutes) };
  }
  return NOTICES[result.outcome === 'unavailable' ? 'error' : result.outcome];
};

const P  = { fontSize: 14, color: G, lineHeight: 1.7, margin: '0 0 16px' };
const H2 = { fontSize: 15, fontWeight: 600, color: '#fff', margin: '0 0 10px' };
const SECTION = { margin: '0 0 24px', padding: 20, background: CARD, border: `1px solid ${BORD}`, borderRadius: 12 };
const SMALL = { fontSize: 13, color: G, lineHeight: 1.6 };

const Page = ({ children }) => (
  <div style={{ minHeight: '100vh', background: BG, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px 16px' }}>
    <div style={{ maxWidth: 560, width: '100%' }}>
      <div style={{ textAlign: 'center', marginBottom: 32 }}>
        <span style={{ fontSize: 22, fontWeight: 700, color: S, letterSpacing: 1 }}>SafeHeir</span>
      </div>
      <div style={{ background: CARD, border: `1px solid ${BORD}`, borderRadius: 16, padding: '36px 32px' }}>
        {children}
      </div>
    </div>
    <style>{`@keyframes spin { to { transform: rotate(360deg); } }
      .sh-file { width: 100%; color: ${G}; font-size: 13px; }
      .sh-file::file-selector-button {
        margin-right: 12px; padding: 8px 14px; border-radius: 8px; cursor: pointer;
        background: transparent; color: #fff; border: 1px solid ${BORD}; font-size: 13px;
      }
      .sh-file:disabled::file-selector-button { cursor: not-allowed; opacity: 0.5; }`}</style>
  </div>
);

const Loading = () => (
  <div role="status" style={{ textAlign: 'center', padding: '24px 0' }}>
    <div aria-hidden="true" style={{
      width: 36, height: 36, border: '3px solid rgba(255,255,255,0.1)',
      borderTopColor: S, borderRadius: '50%',
      animation: 'spin 0.8s linear infinite', margin: '0 auto 16px',
    }} />
    <p style={{ color: G, fontSize: 14, margin: 0 }}>Vérification du lien...</p>
  </div>
);

const Notice = ({ tone, title, text, onRetry }) => (
  <div style={{ textAlign: 'center' }}>
    <div aria-hidden="true" style={{
      width: 56, height: 56, border: `1px solid ${tone}`, borderRadius: 14,
      display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px',
    }}>
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
        {tone === ERR
          ? <path d="M18 6L6 18M6 6l12 12" stroke={tone} strokeWidth="2.5" strokeLinecap="round" />
          : <path d="M12 7v6M12 17h.01" stroke={tone} strokeWidth="2.5" strokeLinecap="round" />}
      </svg>
    </div>
    <h1 style={{ fontSize: 18, fontWeight: 700, color: '#fff', margin: '0 0 10px' }}>{title}</h1>
    <p style={{ fontSize: 14, color: G, margin: 0, lineHeight: 1.6 }}>{text}</p>
    {onRetry && (
      <button type="button" onClick={onRetry} style={{
        marginTop: 24, width: '100%', padding: '14px 0', background: S, color: BG,
        border: 'none', borderRadius: 10, fontWeight: 700, fontSize: 15, cursor: 'pointer',
      }}>
        Réessayer
      </button>
    )}
  </div>
);

const FileField = ({ id, label, disabled, onChange }) => (
  <div style={{ margin: '0 0 16px' }}>
    <label htmlFor={id} style={{ display: 'block', fontSize: 14, fontWeight: 600, color: '#fff', margin: '0 0 8px' }}>
      {label}
    </label>
    <input
      id={id}
      type="file"
      className="sh-file"
      accept="application/pdf,image/jpeg,image/png,.pdf,.jpg,.jpeg,.png"
      disabled={disabled}
      onChange={(event) => onChange(event.target.files?.[0] ?? null)}
    />
  </div>
);

// Formulaire de dépôt. Le verrou (ref) garantit un seul appel même si deux clics
// arrivent avant que le bouton désactivé soit rendu.
const DepositForm = ({ token, onSubmitted, onLinkRefused }) => {
  const [files, setFiles] = useState({ deathCertificate: null, identityDocument: null });
  const [sending, setSending] = useState(false);
  const [failure, setFailure] = useState(null);
  const lock = useRef(false);

  const pick = (key) => (file) => {
    setFiles((current) => ({ ...current, [key]: file }));
    setFailure(null);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (lock.current) return;
    lock.current = true;
    setSending(true);
    setFailure(null);
    const res = await submitTransmissionDocuments(token, files);
    if (res.outcome === 'submitted') {
      onSubmitted(res.submittedAt);
      return;
    }
    if (LINK_REFUSALS.includes(res.outcome)) {
      onLinkRefused(res.outcome);
      return;
    }
    const message = DEPOSIT_ERRORS[res.outcome] ?? DEPOSIT_ERRORS.error;
    setFailure(typeof message === 'function' ? message(res.retryAfterMinutes) : message);
    setSending(false);
    lock.current = false;
  };

  return (
    <form onSubmit={handleSubmit} noValidate style={SECTION}>
      <h2 style={H2}>{DEPOSIT.title}</h2>
      <p style={{ ...P, margin: '0 0 20px' }}>{DEPOSIT.intro}</p>
      <FileField id="death-certificate" label={DEPOSIT.certificateLabel} disabled={sending}
        onChange={pick('deathCertificate')} />
      <FileField id="identity-document" label={DEPOSIT.identityLabel} disabled={sending}
        onChange={pick('identityDocument')} />
      <p style={{ ...SMALL, margin: '0 0 4px' }}>{DEPOSIT.formats}</p>
      <p style={{ ...SMALL, margin: '0 0 8px' }}>
        {DEPOSIT.privacyLead.before}
        <Link to="/legal/mentions-legales" style={{ color: S }}>{DEPOSIT.privacyLead.link}</Link>
        {DEPOSIT.privacyLead.after}
      </p>
      {DEPOSIT.privacy.map((line, i) => (
        <p key={line} style={{ ...SMALL, margin: i === DEPOSIT.privacy.length - 1 ? '0 0 20px' : '0 0 8px' }}>
          {line}
        </p>
      ))}
      <button type="submit" disabled={sending} style={{
        width: '100%', padding: '14px 0', background: S, color: BG, border: 'none', borderRadius: 10,
        fontWeight: 700, fontSize: 15, cursor: sending ? 'not-allowed' : 'pointer', opacity: sending ? 0.6 : 1,
      }}>
        {DEPOSIT.submit}
      </button>
      {sending && (
        <p role="status" style={{ fontSize: 14, color: G, margin: '12px 0 0', textAlign: 'center' }}>
          {DEPOSIT.sending}
        </p>
      )}
      {failure && (
        <p role="alert" style={{ fontSize: 14, color: ERR, lineHeight: 1.6, margin: '12px 0 0' }}>{failure}</p>
      )}
    </form>
  );
};

// Accusé de réception (après dépôt) et dossier en cours d'examen (à l'ouverture).
const ReceivedPanel = ({ title, lines }) => (
  <section style={SECTION}>
    <h2 style={H2}>{title}</h2>
    {lines.map((line, i) => (
      <p key={line} style={{ ...P, margin: i === lines.length - 1 ? 0 : P.margin }}>{line}</p>
    ))}
  </section>
);

const ValidScreen = ({ owner, expiresAt, status, submittedAt, token, onSubmitted, onLinkRefused }) => (
  <>
    <h1 style={{ fontSize: 20, fontWeight: 700, color: '#fff', margin: '0 0 16px', lineHeight: 1.3 }}>
      {titleFor(owner)}
    </h1>
    <p style={P}>{introFor(owner)}</p>
    <p style={P}>{validityFor(expiresAt)}</p>

    {status === 'submitted' && (
      <ReceivedPanel title={ACK.title}
        lines={[ACK.received(formatLongDate(submittedAt)), ACK.next, REPORT_MISTAKE]} />
    )}
    {status === 'pending_review' && (
      <ReceivedPanel title={REVIEW.title}
        lines={[REVIEW.received(formatLongDate(submittedAt)), NO_ACTION, REPORT_MISTAKE]} />
    )}
    {status === 'awaiting_documents' && (
      <>
        <h2 style={H2}>Documents à déposer</h2>
        <ul style={{ margin: '0 0 24px', paddingLeft: 20, color: G, fontSize: 14, lineHeight: 1.7 }}>
          {DOCUMENTS.map((doc) => <li key={doc}>{doc}</li>)}
        </ul>
        <DepositForm token={token} onSubmitted={onSubmitted} onLinkRefused={onLinkRefused} />
      </>
    )}

    <div role="note" style={{
      display: 'flex', gap: 14, alignItems: 'flex-start', padding: 16, margin: '0 0 24px',
      background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.25)', borderRadius: 12,
    }}>
      <div aria-hidden="true" style={{
        flexShrink: 0, width: 36, height: 36, borderRadius: 10, background: 'rgba(245,158,11,0.12)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" stroke={WARN} strokeWidth="2"
            strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
      <div>
        <h2 style={{ ...H2, margin: '0 0 6px' }}>{CODE_NOTICE.title}</h2>
        <p style={{ fontSize: 14, color: G, lineHeight: 1.6, margin: 0 }}>{CODE_NOTICE.text}</p>
      </div>
    </div>

    <p style={P}>{reportFor(owner)}</p>

    <footer style={{ borderTop: `1px solid ${BORD}`, paddingTop: 20, marginTop: 8 }}>
      {FOOTER.map((line) => (
        <p key={line} style={{ fontSize: 12, color: 'rgba(255,255,255,0.35)', lineHeight: 1.6, margin: '0 0 6px' }}>
          {line}
        </p>
      ))}
    </footer>
  </>
);

const TransmissionPage = () => {
  // Lecture pure à l'initialisation : le nettoyage de l'URL se fait dans un effet.
  const [token] = useState(() => readLinkToken(window.location.hash));
  const [result, setResult] = useState(() => (token ? null : { outcome: 'invalid' }));
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (window.location.hash) {
      window.history.replaceState(window.history.state, '', `${window.location.pathname}${window.location.search}`);
    }
  }, []);

  useEffect(() => {
    if (!token) return undefined;
    let active = true;
    setResult(null);
    verifyTransmissionLink(token).then((res) => {
      if (active) setResult(res);
    });
    return () => {
      active = false;
    };
  }, [token, attempt]);

  if (!result) return <Page><Loading /></Page>;
  if (result.outcome === 'valid') {
    return (
      <Page>
        <ValidScreen
          owner={result.ownerFirstName}
          expiresAt={result.expiresAt}
          status={result.status}
          submittedAt={result.submittedAt}
          token={token}
          // Accusé de réception sur la date rendue par submit : pas de nouvel appel à verify.
          onSubmitted={(submittedAt) => setResult((current) => ({ ...current, status: 'submitted', submittedAt }))}
          onLinkRefused={(outcome) => setResult({ outcome })}
        />
      </Page>
    );
  }
  const retryable = result.outcome === 'error' || result.outcome === 'unavailable';
  return (
    <Page>
      <Notice
        {...noticeFor(result)}
        onRetry={retryable ? () => setAttempt((n) => n + 1) : undefined}
      />
    </Page>
  );
};

export default TransmissionPage;
